import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Calculator, Info, FilePlus } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import axios from 'axios';
import { toast } from 'sonner';
import { METIERS_COPPAM } from './simulateurTarifData';
import { MetiersSelection, ResultatCalcul } from './SimulateurShared';
import SimulateurAvenantMode from './SimulateurAvenantMode';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function SimulateurTarif({ 
  onSimulationComplete, 
  initialCA = '',
  initialMetiers = {},
  showActions = true,
  getAuthHeader,
  onAvenantCreated,
  initialMode = 'souscription'
}) {
  const { t } = useTranslation();
  
  // Mode: 'souscription' ou 'avenant'
  const [mode, setMode] = useState(initialMode);
  
  // États communs
  const [chiffreAffaires, setChiffreAffaires] = useState(initialCA);
  const [metiersSelectionnes, setMetiersSelectionnes] = useState(initialMetiers);
  const [resultatCalcul, setResultatCalcul] = useState(null);
  
  // États pour le mode avenant
  const [referenceContrat, setReferenceContrat] = useState('');
  const [searchLoading, setSearchLoading] = useState(false);
  const [contratParent, setContratParent] = useState(null);
  const [contratExpire, setContratExpire] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [motifAvenant, setMotifAvenant] = useState('');
  const [notesAvenant, setNotesAvenant] = useState('');
  const [creatingAvenant, setCreatingAvenant] = useState(false);
  const [avenantStep, setAvenantStep] = useState(1); // 1: Recherche, 2: Calcul, 3: Validation

  const toggleMetier = (categorie, metier) => {
    setMetiersSelectionnes(prev => {
      const key = `${categorie}|||${metier}`;
      const newSelection = { ...prev };
      
      if (newSelection[key]) {
        delete newSelection[key];
      } else {
        newSelection[key] = { categorie, metier };
      }
      
      return newSelection;
    });
    setResultatCalcul(null);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('fr-FR');
  };

  // Rechercher le contrat parent pour avenant
  const searchContrat = async () => {
    if (!referenceContrat.trim()) {
      toast.error('Veuillez saisir une référence de contrat');
      return;
    }

    setSearchLoading(true);
    setErrorMessage('');
    setContratParent(null);
    setContratExpire(false);

    try {
      const headers = getAuthHeader ? getAuthHeader() : {};
      const response = await axios.get(
        `${BACKEND_URL}/api/souscriptions/search-by-reference/${encodeURIComponent(referenceContrat.trim())}`,
        { headers }
      );

      if (response.data.success && response.data.souscription) {
        const souscription = response.data.souscription;
        setContratParent(souscription);

        // Vérifier si le contrat est expiré
        const dateFin = new Date(souscription.date_fin || souscription.periode_adhesion_fin);
        const now = new Date();
        
        if (dateFin < now) {
          setContratExpire(true);
          setErrorMessage(`Ce contrat a expiré le ${formatDate(dateFin)}. Impossible de créer un avenant.`);
        } else if (souscription.statut !== 'validee' && !souscription.paiement_valide) {
          setErrorMessage('Ce contrat doit être validé et payé pour créer un avenant.');
        } else {
          // Pré-remplir le CA si disponible
          if (souscription.chiffre_affaires) {
            setChiffreAffaires(souscription.chiffre_affaires.toString());
          }
          setAvenantStep(2);
          toast.success('Contrat trouvé ! Vous pouvez maintenant calculer la tarification.');
        }
      } else {
        setErrorMessage('Contrat non trouvé. Vérifiez la référence saisie.');
      }
    } catch (error) {
      if (error.response?.status === 404) {
        setErrorMessage('Contrat non trouvé. Vérifiez la référence saisie.');
      } else {
        setErrorMessage(error.response?.data?.detail || 'Erreur lors de la recherche du contrat');
      }
    } finally {
      setSearchLoading(false);
    }
  };

  const calculerTarif = () => {
    const ca = parseFloat(chiffreAffaires);
    if (isNaN(ca) || ca <= 0) {
      toast.error("Veuillez saisir un chiffre d'affaires valide");
      return;
    }

    const coefficients = [];
    Object.values(metiersSelectionnes).forEach(({ categorie, metier }) => {
      const coef = METIERS_COPPAM[categorie]?.metiers[metier]?.coefficient;
      if (coef) coefficients.push(coef);
    });

    if (coefficients.length === 0) {
      toast.error("Veuillez sélectionner au moins un métier");
      return;
    }

    const coefficientMoyen = coefficients.reduce((a, b) => a + b, 0) / coefficients.length;
    const montantHT = ca * (coefficientMoyen / 100);
    const tva = montantHT * 0.085;
    const montantTTC = montantHT + tva;

    const resultat = {
      chiffreAffaires: ca,
      metiersSelectionnes,
      coefficientMoyen,
      montantHT,
      tva,
      montantTTC,
      nombreMetiers: coefficients.length
    };

    setResultatCalcul(resultat);

    // Callback pour l'intégration (mode souscription)
    if (mode === 'souscription' && onSimulationComplete) {
      onSimulationComplete(resultat);
    }
  };

  // Créer l'avenant
  const createAvenant = async () => {
    if (!contratParent || !resultatCalcul) {
      toast.error('Données manquantes pour créer l\'avenant');
      return;
    }

    setCreatingAvenant(true);
    try {
      const headers = getAuthHeader ? getAuthHeader() : {};
      const souscriptionId = contratParent.id || contratParent.numero_police;
      
      const response = await axios.post(
        `${BACKEND_URL}/api/souscriptions/admin/${souscriptionId}/avenant`,
        {
          montant_ht_contribution: resultatCalcul.montantHT,
          chiffre_affaires: resultatCalcul.chiffreAffaires,
          activites_selectionnees: metiersSelectionnes,
          motif: motifAvenant || 'Avenant - Extension/Modification activités',
          notes: notesAvenant,
          tarification: {
            coefficient_moyen: resultatCalcul.coefficientMoyen,
            montant_ht: resultatCalcul.montantHT,
            tva: resultatCalcul.tva,
            montant_ttc: resultatCalcul.montantTTC
          }
        },
        { headers }
      );

      if (response.data.success) {
        toast.success(`Avenant ${response.data.avenant?.reference} créé avec succès !`);
        
        // Callback
        if (onAvenantCreated) {
          onAvenantCreated(response.data.avenant);
        }
        
        // Reset form
        resetAvenantForm();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la création de l\'avenant');
    } finally {
      setCreatingAvenant(false);
    }
  };

  const resetAvenantForm = () => {
    setAvenantStep(1);
    setReferenceContrat('');
    setContratParent(null);
    setContratExpire(false);
    setErrorMessage('');
    setChiffreAffaires('');
    setMetiersSelectionnes({});
    setResultatCalcul(null);
    setMotifAvenant('');
    setNotesAvenant('');
  };

  // Mode Souscription classique
  const renderSouscriptionMode = () => (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Calculator className="h-6 w-6 text-blue-600" />
            <CardTitle>{t('simulateur.title', 'Simulateur de Tarification')}</CardTitle>
          </div>
          <CardDescription>
            {t('simulateur.description', 'Estimez votre contribution en fonction de votre chiffre d\'affaires et de vos activités')}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Chiffre d'affaires */}
          <div>
            <Label htmlFor="ca">{t('simulateur.ca_label', 'Chiffre d\'Affaires Annuel (€)')}</Label>
            <Input
              id="ca"
              type="number"
              placeholder="Ex: 500000"
              value={chiffreAffaires}
              onChange={(e) => {
                setChiffreAffaires(e.target.value);
                setResultatCalcul(null);
              }}
              className="mt-1"
            />
          </div>

          {/* Sélection métiers */}
          <MetiersSelection metiersSelectionnes={metiersSelectionnes} toggleMetier={toggleMetier} t={t} />

          {/* Bouton calcul */}
          {showActions && (
            <Button 
              onClick={calculerTarif} 
              className="w-full"
              disabled={!chiffreAffaires || Object.keys(metiersSelectionnes).length === 0}
            >
              <Calculator className="mr-2 h-4 w-4" />
              Calculer la Tarification
            </Button>
          )}
        </CardContent>
      </Card>

      {/* Résultat */}
      <ResultatCalcul resultatCalcul={resultatCalcul} mode={mode} />

      {resultatCalcul && (
        <div className="bg-blue-600 border border-blue-700 rounded-lg p-3 flex items-start gap-2">
          <Info className="h-5 w-5 text-white mt-0.5 flex-shrink-0" />
          <p className="text-sm text-white">
            Cette estimation est basée sur votre chiffre d'affaires et les métiers sélectionnés. 
            Le tarif final sera confirmé lors de la souscription.
          </p>
        </div>
      )}
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Tabs pour choisir le mode */}
      <Tabs value={mode} onValueChange={(v) => {
        setMode(v);
        // Reset les états lors du changement de mode
        setResultatCalcul(null);
        if (v === 'souscription') {
          resetAvenantForm();
        }
      }}>
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="souscription" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white">
            <Calculator className="h-4 w-4 mr-2" />
            Nouvelle Souscription
          </TabsTrigger>
          <TabsTrigger value="avenant" className="data-[state=active]:bg-purple-600 data-[state=active]:text-white">
            <FilePlus className="h-4 w-4 mr-2" />
            Créer un Avenant
          </TabsTrigger>
        </TabsList>

        <TabsContent value="souscription" className="mt-6">
          {renderSouscriptionMode()}
        </TabsContent>

        <TabsContent value="avenant" className="mt-6">
          <SimulateurAvenantMode
            avenantStep={avenantStep}
            setAvenantStep={setAvenantStep}
            referenceContrat={referenceContrat}
            setReferenceContrat={setReferenceContrat}
            searchContrat={searchContrat}
            searchLoading={searchLoading}
            errorMessage={errorMessage}
            contratExpire={contratExpire}
            contratParent={contratParent}
            chiffreAffaires={chiffreAffaires}
            setChiffreAffaires={setChiffreAffaires}
            resultatCalcul={resultatCalcul}
            setResultatCalcul={setResultatCalcul}
            calculerTarif={calculerTarif}
            metiersSelectionnes={metiersSelectionnes}
            toggleMetier={toggleMetier}
            motifAvenant={motifAvenant}
            setMotifAvenant={setMotifAvenant}
            notesAvenant={notesAvenant}
            setNotesAvenant={setNotesAvenant}
            createAvenant={createAvenant}
            creatingAvenant={creatingAvenant}
            formatDate={formatDate}
            t={t}
            setContratParent={setContratParent}
            setErrorMessage={setErrorMessage}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}
